import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Play, Calendar, Users, ShieldCheck, Sparkles, Layers, PlayCircle } from "lucide-react";
import { speakingSessions as speakMock, speakingTopics, images } from "../mock/mock";
import { useCollection } from "../hooks/useCollection";

const formats = [
  { t: "Keynote", c: "Framing, strategy, and narrative for main-stage audiences. 20–45 minutes." },
  { t: "Workshop", c: "Hands-on sessions for engineering teams. Half-day or full-day, outcome-led." },
  { t: "Fireside / Panel", c: "Candid conversations on AI, security, and engineering leadership." },
  { t: "Masterclass", c: "Deep technical walk-through with frameworks, code, and live teardown." },
];

export default function Speaking() {
  const { data: speakingSessions } = useCollection("speaking", speakMock);
  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO */}
      <section className="relative pt-16 md:pt-24 pb-16 md:pb-24 grain overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="tag-mono mb-6">[ Speaking ]</div>
            <h1 className="headline-xl">
              Enabling action, <span className="italic-serif text-[var(--molten)]">not just ideas.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[var(--ink)]/75 max-w-2xl leading-relaxed">
              Backed by experience across engineering leadership, community impact, and AI transformation — sessions that leave teams with frameworks they can use on Monday.
            </p>
            <div className="mt-10 flex gap-3 flex-wrap">
              <Link to="/contact" className="btn-ink">Invite me to speak <Mic size={15} /></Link>
              <a href="https://topmate.io/drijesh" target="_blank" rel="noreferrer" className="btn-outline">Book a call <ArrowUpRight size={16} /></a>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={images.speakingHero} alt="Keynote" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 bg-[var(--ink)]/90 text-[var(--cream)] px-4 py-3">
                <PlayCircle size={22} className="text-[var(--molten)]" />
                <span className="font-mono text-xs uppercase tracking-[0.14em]">Live from DevFest · Main Stage</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT PILLARS */}
      <section className="section-ink grain py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <div className="tag-mono mb-4">[ Impact delivered ]</div>
              <h2 className="headline-lg text-[var(--cream)]">
                A session is <span className="italic-serif text-[var(--molten)]">only worth it</span> if something changes after.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 space-y-6">
              <div className="rise flex gap-4 p-6 border border-[var(--cream)]/15">
                <Layers size={22} className="text-[var(--molten)] mt-1 shrink-0" />
                <div>
                  <div className="font-serif text-xl text-[var(--cream)]">Enabled engineers to adopt AI in real workflows</div>
                  <p className="mt-2 text-[var(--cream)]/70 leading-relaxed">From prompt hygiene to SDLC integration — no hand-waving.</p>
                </div>
              </div>
              <div className="rise flex gap-4 p-6 border border-[var(--cream)]/15">
                <ShieldCheck size={22} className="text-[var(--molten)] mt-1 shrink-0" />
                <div>
                  <div className="font-serif text-xl text-[var(--cream)]">Helped teams rethink DevSecOps</div>
                  <p className="mt-2 text-[var(--cream)]/70 leading-relaxed">Security as a design primitive, not a ticketing queue.</p>
                </div>
              </div>
              <div className="rise flex gap-4 p-6 border border-[var(--cream)]/15">
                <Sparkles size={22} className="text-[var(--molten)] mt-1 shrink-0" />
                <div>
                  <div className="font-serif text-xl text-[var(--cream)]">Practical frameworks, not concepts</div>
                  <p className="mt-2 text-[var(--cream)]/70 leading-relaxed">Templates, checklists, and mental models teams keep using.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Signature topics ]</div>
          <h2 className="headline-lg mb-12 max-w-3xl">
            Picked for <span className="italic-serif text-[var(--molten)]">decision-makers</span> and the engineers who work with them.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[var(--ink)]/15">
            {speakingTopics.map((t, i) => (
              <div
                key={i}
                className={`rise flex items-center gap-5 py-6 border-b border-[var(--ink)]/15 ${
                  i % 2 === 0 ? "md:border-r md:pr-10" : "md:pl-10"
                }`}
              >
                <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-xl md:text-2xl">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAST SESSIONS */}
      <section className="py-24 md:py-32 bg-[var(--cream-soft)] grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="tag-mono mb-4">[ Selected sessions ]</div>
              <h2 className="headline-lg">Past <span className="italic-serif text-[var(--molten)]">stages.</span></h2>
            </div>
            <div className="tag-mono flex items-center gap-2">
              <Calendar size={14} /> Keynotes · Podcasts · Workshops
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {speakingSessions.map((s, i) => (
              <a
                key={i}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="rise p-7 bg-[var(--cream)] border border-[var(--ink)]/15 hover:border-[var(--molten)] transition-colors group"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="tag-mono">{s.venue}</span>
                  <Play size={16} className="text-[var(--molten)]" />
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight group-hover:text-[var(--molten)] transition-colors">{s.title}</h3>
                <p className="mt-3 text-[var(--ink)]/70">{s.topic}</p>
                <div className="mt-6 flex items-center gap-2 tag-mono">
                  Watch <ArrowUpRight size={14} />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag-mono mb-4">[ Formats offered ]</div>
          <h2 className="headline-lg mb-12 max-w-3xl">
            Designed to <span className="italic-serif text-[var(--molten)]">fit your</span> audience, your time, your goals.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-[var(--ink)]/15">
            {formats.map((f, i) => (
              <div key={i} className={`rise p-8 border-b border-[var(--ink)]/15 ${i !== formats.length - 1 ? "md:border-r" : ""}`}>
                <Mic size={20} className="text-[var(--molten)] mb-6" />
                <div className="font-serif text-xl md:text-2xl mb-3">{f.t}</div>
                <p className="text-[var(--ink)]/70 leading-relaxed text-sm">{f.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
